import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";

import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import DashboardLayout from "@/components/layout/DashboardLayout";
import Home from "@/pages/Home";
import CalendarPage from "@/pages/CalendarPage";
import Finance from "@/pages/Finance";
import Mailchimp from "@/pages/Mailchimp";
import KPI from "@/pages/KPI";
import Documents from "@/pages/Documents";
import StaffDirectory from "@/pages/StaffDirectory";
import People from "@/pages/People";
import Receipts from "@/pages/Receipts";
import AuditLogPage from "@/pages/AuditLog";
import Settings from "@/pages/Settings";
import OAuthCallbackBridge from "@/pages/OAuthCallbackBridge";
import KPIDiagnostics from "@/pages/KPIDiagnostics";
import PrivacyPolicy from "@/pages/legal/PrivacyPolicy";
import TermsOfUse from "@/pages/legal/TermsOfUse";

function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-cream">
        <div className="h-10 w-10 rounded-full border-2 border-terracotta border-t-transparent animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function AppRouter() {
  // Synchronous check — handles session_id BEFORE other routing
  if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      {/* Public legal pages — MUST remain accessible without authentication.
          Required by Intuit for QuickBooks production app review. */}
      <Route path="/legal/privacy" element={<PrivacyPolicy />} />
      <Route path="/legal/terms" element={<TermsOfUse />} />
      {/* OAuth safety-net: if Google's redirect_uri is misconfigured WITHOUT
          the /api/ prefix, Google lands users HERE (frontend) instead of
          the backend handler. This bridge forwards to /api/oauth/calendar/
          callback with the full query string so the OAuth flow still
          completes. Once GOOGLE_CALENDAR_REDIRECT_URI is fixed in the
          deploy env vars, this route never runs in practice. */}
      <Route path="/oauth/calendar/callback" element={<OAuthCallbackBridge />} />
      <Route path="/oauth/google/callback" element={<OAuthCallbackBridge />} />
      <Route
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<Home />} />
        <Route path="/calendar" element={<CalendarPage />} />
        <Route path="/finance" element={<Finance />} />
        {/* Legacy /giving → /finance so bookmarks + external links keep working. */}
        <Route path="/giving" element={<Navigate to="/finance#giving" replace />} />
        <Route path="/mailchimp" element={<Mailchimp />} />
        <Route path="/kpi" element={<KPI />} />
        <Route path="/documents" element={<Documents />} />
        <Route path="/staff" element={<StaffDirectory />} />
        <Route path="/people" element={<People />} />
        <Route path="/receipts" element={<Receipts />} />
        <Route
          path="/audit-log"
          element={
            <ProtectedRoute roles={["admin"]}>
              <AuditLogPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute roles={["admin"]}>
              <Settings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/kpi-diagnostics"
          element={
            <ProtectedRoute roles={["admin"]}>
              <KPIDiagnostics />
            </ProtectedRoute>
          }
        />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
